import { type HTMLAttributes, useId } from 'react';
import {
  CDN_IMAGE_WIDTHS,
  FLAG_IMAGE_BOUNDS,
  flagSvgPaths,
  goldGradientStops
} from './flagSvgPaths';

export type FlagSize = 'sm' | 'md' | 'lg';

export interface FlagProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * ISO 3166-1-alpha-2 country code (e.g., 'mg', 'fr', 'cm', 'bf')
   * See: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
   */
  countryCode: string;

  /**
   * Predefined size variants
   * @default 'md'
   */
  size?: FlagSize;

  /**
   * Accessible label for the flag image
   * If not provided, defaults to "{COUNTRY_CODE} flag"
   */
  alt?: string;

  /**
   * Additional CSS classes for custom sizing/styling
   */
  className?: string;
}

const sizeClasses: Record<FlagSize, string> = {
  sm: 'w-20 h-7', // Small: ~80x28px (724:254 ratio)
  md: 'w-32 h-11', // Medium: ~128x44px
  lg: 'w-48 h-[67px]' // Large: ~192x67px
};

/**
 * Flag Component
 *
 * Displays country flags using the FlagCDN API (https://flagcdn.com/) with a 3D parallelogram design
 * matching the LUDOKA game aesthetic. Uses SVG paths exported from Figma for pixel-perfect accuracy.
 *
 * Features a gold gradient border, black shadow, and chevron-shaped parallelogram.
 *
 * @example
 * ```tsx
 * <Flag countryCode="mg" size="md" />
 * <Flag countryCode="fr" size="lg" alt="France" />
 * <Flag countryCode="cm" className="w-40" />
 * ```
 */
export function Flag({ countryCode, size = 'md', alt, className = '', ...rest }: FlagProps) {
  const normalizedCode = countryCode.toLowerCase().trim();
  const cdnWidth = CDN_IMAGE_WIDTHS[size];
  const flagUrl = `https://flagcdn.com/w${String(cdnWidth)}/${normalizedCode}.png`;
  const defaultAlt = alt ?? `${countryCode.toUpperCase()} flag`;
  const gradientId = useId();
  const clipPathId = useId();

  const sizeClass = sizeClasses[size];

  return (
    <div
      className={`${sizeClass} relative ${className}`.trim()}
      role="img"
      aria-label={defaultAlt}
      {...rest}
    >
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 724 254">
        <defs>
          {/* Gold gradient for border */}
          <linearGradient
            id={gradientId}
            x1="17.32"
            x2="706.44"
            y1="124.3"
            y2="124.3"
            gradientUnits="userSpaceOnUse"
          >
            {goldGradientStops.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>

          {/* Clip path for flag image */}
          <clipPath id={clipPathId}>
            <path d={flagSvgPaths.flagArea} />
          </clipPath>
        </defs>

        {/* Black shadow layer (bottom) */}
        <path d={flagSvgPaths.shadow} fill="black" />

        {/* Gold gradient border layer (middle) */}
        <path d={flagSvgPaths.border} fill={`url(#${gradientId})`} />

        {/* White background layer */}
        <path d={flagSvgPaths.flagArea} fill="white" />

        {/* Flag image (clipped to parallelogram shape) */}
        <image
          href={flagUrl}
          x={FLAG_IMAGE_BOUNDS.x}
          y={FLAG_IMAGE_BOUNDS.y}
          width={FLAG_IMAGE_BOUNDS.width}
          height={FLAG_IMAGE_BOUNDS.height}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipPathId})`}
          aria-label={defaultAlt}
        />
      </svg>
    </div>
  );
}
